import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios"; import { handleAuthError } from "../../utils";
import withAuth from "../withAuth";
import {
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Badge,
  Spinner,
  Table,
  Button,
  Input,
  FormGroup,
  Label
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from '../../Network';
import { usePagination } from "../../hooks/usePagination";
import PaginationControls from "../../components/Common/PaginationControls";

const AdminActivity = () => {
  document.title = "Admin Activity | RYD Admin";

  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [parentsModal, setParentsModal] = useState(false);
  const [assignedParents, setAssignedParents] = useState([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [unassignedParents, setUnassignedParents] = useState([]);
  const [assignModal, setAssignModal] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSecondaryView, setIsSecondaryView] = useState(false);
  const [secondaryAssignedParents, setSecondaryAssignedParents] = useState([]);

  const {
    paginatedData: paginatedAdmins,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    showAll,
    handlePageChange,
    handlePageSizeChange,
    goToNextPage,
    goToPreviousPage,
    canPreviousPage,
    canNextPage,
    paginatedDataLength,
  } = usePagination(admins, 20);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/admin/activity/stats`);
      setAdmins(response.data.data.admins);
      setStats(response.data.data.stats);
      setLoading(false);
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      toast.error("Failed to fetch admin data");
      setLoading(false);
    }
  };

  const fetchAssignedParents = async (adminId, isSecondary = false) => {
    try {
      setLoadingParents(true);
      const endpoint = isSecondary
        ? `${baseUrl}/admin/activity/parents/secondary/${adminId}`
        : `${baseUrl}/admin/activity/parents/${adminId}`;

      const response = await axios.get(endpoint);

      if (isSecondary) {
        setSecondaryAssignedParents(response.data.data.parents);
      } else {
        setAssignedParents(response.data.data.parents);
      }

      setLoadingParents(false);
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      toast.error("Failed to fetch assigned parents");
      setLoadingParents(false);
    }
  };

  const handleViewParents = (admin, isSecondary = false) => {
    setSelectedAdmin(admin);
    setIsSecondaryView(isSecondary);
    fetchAssignedParents(admin.id, isSecondary);
    setParentsModal(true);
  };


const unassignParent = async (parentId) => {
  try {
    setLoadingParents(true);
    const endpoint = isSecondaryView 
      ? `${baseUrl}/admin/activity/secondary/unassigned`
      : `${baseUrl}/admin/activity/unassigned`;
      
    await axios.post(endpoint, {
      parentId
    });
    
    toast.success(`Parent ${isSecondaryView ? 'secondary ' : ''}unassigned successfully`);
    fetchAssignedParents(selectedAdmin.id, isSecondaryView);
    fetchAdminData();
    setLoadingParents(false);
  } catch (error) { handleAuthError(error)
    console.error("Error:", error);
    toast.error(`Failed to ${isSecondaryView ? 'secondary ' : ''}unassign parent`);
    setLoadingParents(false);
  }
};

  const toggleParentsModal = () => {
    setParentsModal(!parentsModal);
  };


  const metricsData = [
    { title: "Assigned Parents", count: stats.totalAssignedParents || 0, statusColor: "success" },
    { title: "Active Parents", count: stats.totalActiveParents || 0, statusColor: "info" },
    { title: "Inactive Parents", count: stats.totalInactiveParents || 0, statusColor: "warning" },
    { title: "Unassigned Parents", count: stats.totalUnassignedParents || 0, statusColor: "danger" },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Admin Activity" />
          <Row>
            <Col lg="12">
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="mb-3">
                    <h5 className="card-title">
                      Admin List{" "}
                      <span className="text-muted fw-normal ms-2">
                        ({admins.length})
                      </span>
                    </h5>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                    <div>
                      <button
                        className="btn btn-primary"
                        onClick={fetchAdminData}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sync-alt me-2"></i>
                            Refresh
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                {metricsData.map((card, index) => (
                  <Col key={index} xl={3} md={6}>
                    <Card className="card-h-100">
                      <CardBody>
                        <h6 className="mb-3">{card.title}</h6>
                        <div className={"badge bg-" + card.statusColor + "-subtle text-" + card.statusColor + " mb-3"}
                          style={{ fontSize: "25px", padding: "10px" }}>
                          {card.count}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {loading ? (
                <div className="text-center mt-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive mt-4 shadow rounded" style={{ backgroundColor: '#f9f9fb' }}>
                  <Table className="table align-middle table-hover">
                    <thead className="bg-primary text-white" style={{ borderRadius: '12px' }}>
                      <tr>
                        <th>#</th>
                        <th>Admin Name</th>
                        <th>Email</th>
                        <th>Total Parents</th>
                        <th>Active Parents</th>
                        <th>Inactive Parents</th>
                        <th>Last Activity</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAdmins.map((admin, index) => {
                        const globalIndex = showAll 
                          ? index 
                          : (currentPage - 1) * pageSize + index;
                        return (
                          <tr
                            key={admin.id || index}
                            className={`align-middle ${globalIndex % 2 === 0 ? 'bg-white' : 'bg-light'}`}
                            style={{ borderBottom: '1px solid #e0e0e0' }}
                          >
                            <td>{globalIndex + 1}</td>
                            <td>{admin.name}</td>
                            <td>{admin.email}</td>
                            <td>
                              <Badge color="primary">{admin.totalParents || 0}</Badge>
                            </td>
                            <td>
                              <Badge color="success">{admin.activeParents || 0}</Badge>
                            </td>
                            <td>
                              <Badge color="warning">{admin.inactiveParents || 0}</Badge>
                            </td>
                            <td>{new Date(admin.lastActivity).toLocaleString() || 'N/A'}</td>
                            <td className="text-center">
                              <div className="d-flex gap-2 justify-content-center">
                                <Link
                                  onClick={() => handleViewParents(admin)}
                                  color="info"
                                  size="sm"
                                  title="View all assignment"
                                  disabled={admin.totalParents === 0}
                                >
                                  <i className="mdi mdi-eye-outline"></i>
                                </Link>
                                <Link
                                  onClick={() => handleViewParents(admin, true)}
                                  className="text-warning"
                                  size="sm"
                                  title="View all secondary assignment"
                                  disabled={admin.totalParents === 0}
                                >
                                  <i className="mdi mdi-eye"></i>
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
              {!loading && admins.length > 0 && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  showAll={showAll}
                  handlePageChange={handlePageChange}
                  handlePageSizeChange={handlePageSizeChange}
                  goToNextPage={goToNextPage}
                  goToPreviousPage={goToPreviousPage}
                  canPreviousPage={canPreviousPage}
                  canNextPage={canNextPage}
                  paginatedDataLength={paginatedDataLength}
                />
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Parents Modal */}
      <Modal isOpen={parentsModal} toggle={toggleParentsModal} size="lg">
        <ModalHeader toggle={toggleParentsModal}>
          {isSecondaryView ? 'Secondary' : ''} Parents Assigned to {selectedAdmin?.name}
        </ModalHeader>
        <ModalBody>
          {loadingParents ? (
            <div className="text-center">
              <Spinner color="primary" />
              <p>Loading parents...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Parent Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Last Active</th>
                    <th>Children</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(isSecondaryView ? secondaryAssignedParents : assignedParents).length > 0 ? (
                    (isSecondaryView ? secondaryAssignedParents : assignedParents).map((parent, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{parent.name}</td>
                        <td>{parent.email}</td>
                        <td>
                          <Badge color={parent.isActive ? "success" : "warning"}>
                            {parent.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td>{parent.lastActive ? new Date(parent.lastActive).toLocaleString() : 'N/A'}</td>
                        <td>{parent.childrenCount}</td>
                        <td>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => unassignParent(parent.id)}
                            disabled={loadingParents}
                          >
                            {loadingParents ? (
                              <Spinner size="sm" />
                            ) : (
                              "Unassign"
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No {isSecondaryView ? 'secondary ' : ''}parents assigned to this admin
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(AdminActivity);